import {
  Heading,
  Link as InternLink,
  Paragraph,
  Skeleton,
} from "@digdir/designsystemet-react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { httpClient } from "@/api/client";
import type { MålerdataOpplastingerStatus } from "@/api/generated";
import { getInnrapporteringsFristerForPeriodeOptions } from "@/api/generated/@tanstack/react-query.gen";
import {
  addDaysToDate,
  addDaysToNullableDate,
  formatDate,
  getEndOfMonth,
  getMonthStringFromDate,
} from "@/utils/dateTimeUtils";
import { useKjøreplaner } from "./useKjøreplaner";

const fristHasPassed = (
  fristInklusiv?: Date | null,
  ekstraordinæreFristerInklusive?: Date[],
): boolean => {
  if (
    fristInklusiv == null &&
    (ekstraordinæreFristerInklusive == null ||
      ekstraordinæreFristerInklusive.length === 0)
  ) {
    return false;
  }

  const now = new Date();

  const frister = [
    fristInklusiv,
    ...(ekstraordinæreFristerInklusive ?? []),
  ].filter((f) => f != null);

  const withinAnyFrist = frister.some((f) => f >= now);
  return !withinAnyFrist;
};

const periodeHasPassed = (month: number, year: number): boolean => {
  const now = new Date();
  const periodeDate = new Date(year, month - 1, 1);
  const lastPossibleDateForOppgjør = getEndOfMonth(
    new Date(periodeDate.getFullYear(), periodeDate.getMonth() + 1, 1),
  );

  return now > lastPossibleDateForOppgjør;
};

export const InnrapporteringsFrist = ({
  month,
  year,
  målerdataOpplastingerStatus,
}: {
  month: number;
  year: number;
  målerdataOpplastingerStatus?: null | MålerdataOpplastingerStatus;
}) => {
  const { t } = useTranslation();

  const { data, isLoading } = useQuery({
    ...getInnrapporteringsFristerForPeriodeOptions({
      client: httpClient,
      query: {
        year,
        month,
      },
    }),
    retry: false,
  });

  const { nesteKjøreplan } = useKjøreplaner();

  const fristInklusiv =
    data?.innrapporteringsFristEksklusiv != null
      ? addDaysToNullableDate(new Date(data.innrapporteringsFristEksklusiv), -1)
      : null;
  const ekstraordinæreFristerInklusiv =
    data?.ekstraordinæreInnrapporteringsFristerEksklusiv != null
      ? data?.ekstraordinæreInnrapporteringsFristerEksklusiv
          ?.map((f1) => (f1 ? addDaysToNullableDate(new Date(f1), -1) : null))
          .filter((f2) => f2 != null)
      : null;

  const isOlderPeriode = periodeHasPassed(month, year);
  const fristerHasPassed = fristHasPassed(
    fristInklusiv,
    ekstraordinæreFristerInklusiv ?? [],
  );

  if (!isLoading && !periodeHasPassed && fristInklusiv == null) {
    // Log to Sentry so we can update data if needed
    console.error("Mangler innrapporteringsfrister for periode", {
      month,
      year,
    });
  }

  const målerdataLåst =
    målerdataOpplastingerStatus === "Låst" ||
    målerdataOpplastingerStatus === "Prosessert";
  const oppgjørAlleredeKjørt = isOlderPeriode || fristerHasPassed;
  const manglerKjøreplanForPeriode = fristInklusiv == null;

  return (
    <div className="space-y-3 mt-9">
      <Heading level={3} data-size="xs">
        {t("pages.maaledata-periode.grunnlag.innrapporteringsFrister.tittel")}
      </Heading>
      {isLoading ? (
        <div className="space-y-5">
          <Paragraph>
            <Skeleton width={200} variant="text" />
          </Paragraph>
          <Paragraph>
            <Skeleton width={100} variant="text" />
          </Paragraph>
          <Paragraph>
            <Skeleton width={300} variant="text" />
          </Paragraph>
        </div>
      ) : (
        <>
          {målerdataLåst ? (
            <Paragraph className="text-pretty whitespace-pre-line">
              {t(
                "pages.maaledata-periode.grunnlag.innrapporteringsFrister.målerdata-låst",
              )}
            </Paragraph>
          ) : oppgjørAlleredeKjørt ? (
            <Paragraph className="text-pretty whitespace-pre-line">
              {t(
                "pages.maaledata-periode.grunnlag.innrapporteringsFrister.oppgjør-allerede-kjørt",
                {
                  context:
                    nesteKjøreplan != null
                      ? formatDate(nesteKjøreplan)
                      : t(
                          "pages.maaledata-periode.grunnlag.innrapporteringsFrister.ingen-neste-kjøreplan",
                        ),
                },
              )}
            </Paragraph>
          ) : manglerKjøreplanForPeriode ? (
            <Paragraph className="text-pretty whitespace-pre-line">
              {t(
                "pages.maaledata-periode.grunnlag.innrapporteringsFrister.frist-mangler",
              )}
            </Paragraph>
          ) : (
            <Paragraph className="text-pretty whitespace-pre-line">
              {t(
                "pages.maaledata-periode.grunnlag.innrapporteringsFrister.om-frist",
                {
                  context: formatDate(fristInklusiv),
                },
              )}
            </Paragraph>
          )}
          {data?.periode.fraInklusiv != null &&
            ekstraordinæreFristerInklusiv &&
            ekstraordinæreFristerInklusiv.length > 0 && (
              <Paragraph className="text-pretty whitespace-pre-line mt-4">
                {t(
                  "pages.maaledata-periode.grunnlag.innrapporteringsFrister.ekstraordinære-frister",
                  {
                    periode: getMonthStringFromDate(
                      new Date(data.periode.fraInklusiv),
                    ),
                    frister: ekstraordinæreFristerInklusiv
                      .map((f) => formatDate(addDaysToDate(f, 1))) // +1 to get back to date for kjøring av oppgjør
                      .join(", "),
                  },
                )}
              </Paragraph>
            )}

          <InternLink asChild>
            <Link
              to={"/veiledning"}
              hash="frister-for-innrapportering-av-malerdata"
              hashScrollIntoView={true}
            >
              {t("pages.maaledata-periode.grunnlag.aapne-veiledning")}
            </Link>
          </InternLink>
        </>
      )}
    </div>
  );
};
